import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { SessionStatus } from '~/services/types';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useMomentsQuery');

export const QUERY_KEY = 'moments';

export type MomentsRequest = {
  date: Date;
  periodId: string;
};

export type MomentItem = {
  quote: string;
  context?: string | null;
  explanation: string;
  humorTypes: string[];
};

export type MomentsResponse = {
  session: {
    status: SessionStatus;
  };
  moments: MomentItem[];
};

export function useMoments({ date, periodId }: MomentsRequest) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status, refetch } = useQuery<null | MomentsResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching classroom moments');

      const response = await httpClient.get<MomentsResponse>(`/review/getMoments?${paramsQueryStr}`);
      log.info('Fetched classroom moments successfully', {
        momentCount: response.moments.length,
      });

      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch classroom moments', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status, refetch };
}
